import React, { useState, useEffect } from "react";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import InvoiceForm from "../components/InvoiceForm";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import { useCreateInvoiceHandler } from "../dataHandlers/createInvoiceHandler";
import { usePermissions } from '../../../hooks/usePermissions';
import { CREATE_PURCHASE_INVOICE, CREATE_SALE_INVOICE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function CreateInvoicePage({ isPurchase = false }) {
  useDocumentTitle("Invoice Form");
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  const onInvoiceCreated = () => {
    navigate(isPurchase ? "/purchases?tab=invoices" : "/sales?tab=invoices");
  };

  const { onSubmit, error, isPending } = useCreateInvoiceHandler({
    onSuccess: onInvoiceCreated,
    isPurchase,
  });

  useEffect(() => {
    if (loading) {
      return;
    }
    const createPermission = isPurchase ? CREATE_PURCHASE_INVOICE : CREATE_SALE_INVOICE;
    if (!hasEInvoicePermission(createPermission)) {
      showActionDeniedSnackbar();
      navigate(isPurchase ? '/purchases?tab=invoices' : '/sales?tab=invoices', { replace: true });
    }
  }, [isPurchase, loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const document = location.state?.document;

  const onSaveForm = async (data) => {
    setIsSubmitting(true);
    const payload = {
      ...data,
      status: "active",
    };

    try {
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = async () => {
    navigate(
      location.state?.from ||
        (isPurchase ? "/purchases?tab=invoices" : "/sales?tab=invoices"),
    );
  };


  return (
    <Stack direction="column" sx={{ mt: "-2rem" }}>
      <InvoiceForm
        initialData={document || {}}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={error}
        isDraftButtonEnabled={location.state?.isDraftButtonEnabled}
        isLoading={isPending || isSubmitting}
        isPurchase={isPurchase}
      />
    </Stack>
  );
}
